/**
 * How settings and progress shape the page: the menu theme, the accessibility
 * classes on <html>, and whether the Apple Watch preview is on.
 */
import { getLevel, getTheme, levelForRating, warmStartRating, type ThemePack } from '@dropstack/core';
import type { Settings } from '../../contracts.ts';

/** The level whose theme dresses the menus: the saved rating's level, level 1 for new players. */
export function menuLevel(savedRating: number | null): number {
  // The level the NEXT run starts at (warm start sits 1.5 levels below the saved
  // rating), so the menu chip and theme match what Play actually opens with.
  return levelForRating(warmStartRating(savedRating));
}

export function themeForLevel(level: number): ThemePack {
  return getTheme(getLevel(level).themeId);
}

/** Accessibility settings mirrored as classes on <html> (styles/global.css reads them). */
export const SETTING_CLASSES = {
  largeText: 'large-text',
  highContrast: 'high-contrast',
  reducedMotion: 'reduced-motion',
  leftHanded: 'left-handed',
} as const satisfies Partial<Record<keyof Settings, string>>;

/**
 * Extra classes set alongside the main one, for modules that key off another
 * name: input/handwriting.css reacts to an ancestor `.ds-reduced-motion`.
 */
export const SETTING_CLASS_ALIASES: Partial<Record<keyof typeof SETTING_CLASSES, readonly string[]>> = {
  reducedMotion: ['ds-reduced-motion'],
};

/** [class, on] for every accessibility class (aliases right after their main class). */
export function htmlClassState(settings: Settings): [string, boolean][] {
  return (Object.keys(SETTING_CLASSES) as (keyof typeof SETTING_CLASSES)[]).flatMap((key) =>
    [SETTING_CLASSES[key], ...(SETTING_CLASS_ALIASES[key] ?? [])].map((name): [string, boolean] => [name, settings[key]]),
  );
}

/** Values of the `watch` URL parameter that turn the preview on / off. */
const WATCH_ON = new Set(['', '1', 'true', 'yes', 'on']);
const WATCH_OFF = new Set(['0', 'false', 'no', 'off']);

/**
 * The `?watch=` parameter: true (`?watch`, `?watch=1`, …), false (`?watch=0`, …)
 * or null when absent or unrecognised.
 */
export function watchParam(search: string): boolean | null {
  const value = new URLSearchParams(search).get('watch');
  if (value === null) return null;
  const v = value.trim().toLowerCase();
  if (WATCH_ON.has(v)) return true;
  if (WATCH_OFF.has(v)) return false;
  return null;
}

/** The URL parameter wins when present (so `?watch=0` can turn a saved preview off for one visit). */
export function isWatchMode(search: string, watchPreview: boolean): boolean {
  return watchParam(search) ?? watchPreview;
}

/** The same URL without the `watch` parameter (to leave the preview without a reload). */
export function withoutWatchParam(url: string): string {
  const u = new URL(url);
  u.searchParams.delete('watch');
  return u.pathname + u.search + u.hash;
}
